// theme.js
import { createContext, useMemo } from "react";
import { createTheme } from "@mui/material/styles";

// 🎨 Color Tokens - JvLands Theme
export const tokens = () => ({
  grey: {
    100: "#f8f9fa",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#6c757d",
    700: "#495057",
    800: "#343a40",
    900: "#212529",
  },
  primary: {
    100: "#d7e5eb",
    200: "#afcbd8",
    300: "#87b1c4",
    400: "#5f97b1",
    500: "#37709d",
    600: "#255a7e",
    700: "#19435e",
    800: "#0c2d3f",
    900: "#032E48", // Logo dark blue
  },
  greenAccent: {
    100: "#dbf5ee",
    200: "#b7ebde",
    300: "#94e2cd",
    400: "#70d8bd",
    500: "#4cceac",
    600: "#3da58a",
    700: "#2e7c67",
    800: "#1e5245",
    900: "#0f2922",
  },
  redAccent: {
    100: "#f8d7d5",
    200: "#f1afab",
    300: "#ea8680",
    400: "#e35e56",
    500: "#dc362c",
    600: "#D9251B", // Logo red
    700: "#b81e15",
    800: "#8b160f",
    900: "#5d0f0a",
  },
  blueAccent: {
    100: "#d7e5eb",
    200: "#afcbd8",
    300: "#87b1c4",
    400: "#5f97b1",
    500: "#37709d",
    600: "#255a7e",
    700: "#19435e",
    800: "#0c2d3f",
    900: "#032E48", // Logo dark blue
  },
});

// ⚙️ MUI Theme Settings
export const themeSettings = () => {
  const colors = tokens();
  return {
    palette: {
      mode: "light",
      primary: {
        main: colors.redAccent[600], // Logo red as primary color
      },
      secondary: {
        main: colors.primary[900], // Logo dark blue as secondary color
      },
      neutral: {
        dark: colors.grey[700],
        main: colors.grey[500],
        light: colors.grey[100],
      },
      background: {
        default: "#f8f9fa",
        paper: "#ffffff",
      },
    },
    typography: {
      fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
      fontSize: 12,
      h1: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 40,
        fontWeight: 600,
      },
      h2: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 32,
        fontWeight: 600,
      },
      h3: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 600,
      },
      h4: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 600,
      },
      h5: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 600,
      },
      h6: {
        fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(3, 46, 72, 0.1)',
          },
        },
      },
    },
  };
};

// 🌈 Theme Hook
export const useMode = () => {
  // Create the theme using our settings
  const theme = useMemo(() => createTheme(themeSettings()), []);
  
  // Return just the theme, no color mode needed
  return [theme, { toggleColorMode: () => {} }];
};

// Keep ColorModeContext for compatibility but it's no longer used
export const ColorModeContext = createContext({
  toggleColorMode: () => {},
});
